import { cva, VariantProps } from 'class-variance-authority';
import { ComponentProps, forwardRef, ReactNode, useCallback } from 'react';
import { cn } from '@nexelem/utils';

const chipVariants = cva(
  'box-border inline-flex h-8 items-center gap-2 rounded-md border bg-surface-container px-2 text-t12-sb [&_svg]:text-icon-primary hover:[&_svg]:text-inherit',
  {
    variants: {
      disabled: {
        true: 'cursor-not-allowed border-border-disabled bg-surface-disabled text-text-disabled [&_svg]:text-inherit',
        false:
          'cursor-pointer border-border-container bg-surface-container hover:border-border-hover-lighter hover:bg-surface-focus-lighter hover:text-text-hover focus-visible:outline-4 focus-visible:outline-border-focus-keyboard active:text-text-focus',
      },
      selected: {
        true: 'border-border-focus-lighter bg-surface-focus-lighter text-text-focus [&_svg]:text-inherit',
        false: 'text-text-secondary-darker',
      },
    },
  },
);

type ChipVariants = VariantProps<typeof chipVariants>;

export type ChipProps = {
  label: string;
  StartIcon?: ReactNode;
  EndIcon?: ReactNode;
  className?: string;
  count?: string | number;
} & ChipVariants &
  ComponentProps<'div'>;

const Count = ({ count }: { count: string | number }) => (
  <span className="rounded-[10px] bg-surface-highlight px-3 py-1 text-s11-sb text-text-brand">{count}</span>
);

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  ({ label, StartIcon, EndIcon, disabled, selected, className, count, onClick, ...props }, ref) => {
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      },
      [disabled, onClick],
    );

    return (
      <div
        aria-disabled={disabled ?? false}
        aria-pressed={selected ?? false}
        ref={ref}
        role="button"
        tabIndex={!disabled ? 0 : undefined}
        className={cn(
          chipVariants({ disabled: !!disabled, selected: !!selected }),
          !StartIcon && 'pl-3',
          !EndIcon && 'pr-3',
          className,
        )}
        onClick={disabled ? undefined : onClick}
        onKeyDown={handleKeyDown}
        {...props}
      >
        <div className="flex size-5 shrink-0 items-center justify-center empty:hidden [&>svg]:size-4.5!">
          {StartIcon}
        </div>
        {label}
        {count && <Count count={count} />}
        <div className="flex size-5 items-center justify-center empty:hidden">{EndIcon}</div>
      </div>
    );
  },
);

Chip.displayName = 'Chip';
